import {
    CardUrlState,
    isPolygonFeature,
    MethaneLayerIds,
    StacDateBlock,
    StacFeature,
    UrlSelectionTypes
} from '../../../../../types';
import { formatDateForUrl, getDayEnd } from '../../../../../utils';
import { getFormattedDate, TimeSelectorItem } from '@methanesat/ui-components';
import { centroid, polygon } from '@turf/turf';
import { Dispatch, UnknownAction } from '@reduxjs/toolkit';
import {
    removeAllMapFeatureHighlights,
    resetSelectedFeatureParams,
    setActiveCapture,
    setEmissionsDrawerCardData,
    setTargetDate,
    setSelectedFeatureParams
} from '../../../../../reducers';
import { log } from '@methanesat/log';
import { Point, Polygon, Position } from 'geojson';

export interface handleDayClickProps {
    day: StacDateBlock;
    dispatch: Dispatch<UnknownAction>;
    targetId: string;
    targetGroupId?: string;
    trackDayClick?: (day: StacDateBlock, timestampString: string) => void;
    layerId?: MethaneLayerIds;
}

/**
 * Converts STAC-formatted dates to the format needed for the TimeSelector component.
 *
 * Groups all `dates` by the dates of the global date picker
 * (whether or not it's actually present in the app) and returns the list of groups.
 * Each group will have a dataKey, value, and an onClick event that calls the `handleClick` parameter.
 * Each may have a list of child items, a label, and other UI elements.
 *
 */
export const stacDatesToTimeSelector = ({
    dates,
    handleClick
}: {
    dates: Record<string, StacDateBlock[]> | null;
    handleClick: (arg: StacDateBlock) => void;
}): TimeSelectorItem<StacFeature<Point | Polygon>>[] => {
    if (dates === null) {
        log.warn('stacDatesToTimeSelector: no dates passed in.');
        return [];
    }

    return Object.entries(dates).map(([timespanStart, items]) => {
        return {
            label: getFormattedDate(new Date(timespanStart)),
            dateKey: timespanStart,
            onClick: (arg) => {
                if (typeof arg !== 'number') handleClick(arg as StacDateBlock);
            },
            value: new Date(timespanStart).getTime(),
            items: items
        };
    });
};

/**
 * Handles users clicking on individual days in the "Observations over time" feature.
 * Sets the target date and active capture, and manages the selected feature in drawers.
 * Also fires the relevant analytics event.
 *
 * @day - a StacDateBlock item, with geometry that is either a polygon or a point
 */
export const handleTimeSelectorDayClick = ({
    day,
    dispatch,
    targetId,
    targetGroupId,
    trackDayClick,
    layerId
}: handleDayClickProps) => {
    // setting date to very end of the selected day
    // for consistency with global date, which must set to end of day

    const endOfDay = getDayEnd(day.date).getTime();

    const featurePolygon = isPolygonFeature(day.feature) ? polygon(day.feature.geometry.coordinates) : null;
    const [lng, lat] = featurePolygon
        ? centroid(featurePolygon).geometry.coordinates
        : // if featurePolygon is null, it must be a point
          (day.feature.geometry.coordinates as Position);

    // For point drawers, clear highlights and reset the selected feature. This closes
    // the data drawer, revealing the floating summary
    if (!layerId || layerId === MethaneLayerIds.areaEmissionRaster || layerId === MethaneLayerIds.plumeEmissionRate) {
        dispatch(resetSelectedFeatureParams());
        dispatch(removeAllMapFeatureHighlights());
    }
    // For targets, update selected feature and URL
    else if (layerId === MethaneLayerIds.targets) {
        const urlDate = formatDateForUrl(new Date(endOfDay));

        // TODO: This can be deprecated eventually
        // https://methanesat.atlassian.net/browse/DP-5699
        dispatch(
            setSelectedFeatureParams({
                itemId: day.id,
                coordinates: [lng, lat],
                targetDate: urlDate,
                targetId: targetGroupId || targetId
            })
        );
        dispatch(
            setEmissionsDrawerCardData({
                'selected-lat': lat,
                'selected-lng': lng,
                'selection-type': UrlSelectionTypes.Target,
                targets: [targetGroupId || targetId],
                [`target-${targetGroupId || targetId}-date`]: urlDate
            } as CardUrlState)
        );
    }

    // update target-specific date
    dispatch(setTargetDate({ date: endOfDay, targetId: targetGroupId || targetId }));

    // set the capture that has been activated
    dispatch(
        setActiveCapture({
            targetId: targetGroupId || targetId,
            itemId: day.id
        })
    );

    // fire off an analytics event to track feature usage statistics.
    trackDayClick?.(day, `${endOfDay}`);
};
